import { Controller } from "@hotwired/stimulus"

/**
 * Controller Stimulus pour la modal de confirmation réutilisable
 *
 * Ce controller gère:
 * - Le clonage du template de modal
 * - Le remplissage des données dynamiques
 * - L'animation d'ouverture/fermeture
 * - La validation du texte de confirmation
 * - Le focus trap pour l'accessibilité (RGAA 4.1)
 * - La fermeture via Escape ou clic backdrop
 *
 * Usage (sur le bouton trigger):
 *   data-action="click->confirmation-modal#open"
 *   data-confirmation-modal-title-param="Supprimer la promotion"
 *   data-confirmation-modal-message-param="Cette action est irréversible."
 *   data-confirmation-modal-url-param="/promotions/1"
 *   data-confirmation-modal-variant-param="hard_delete"
 *   data-confirmation-modal-confirm-text-param="Ma Promotion"
 *   data-confirmation-modal-consequences-param='["5 élèves", "12 sessions"]'
 */
export default class extends Controller {
  static targets = [
    "modal",
    "content",
    "header",
    "iconContainer",
    "icon",
    "title",
    "message",
    "consequencesSection",
    "consequencesList",
    "confirmTextSection",
    "confirmTextDisplay",
    "confirmTextInput",
    "warningSection",
    "warningText",
    "softDeleteWarning",
    "form",
    "csrfToken",
    "cancelButton",
    "submitButton",
    "submitIcon",
    "submitText"
  ]

  static values = {
    animationDuration: { type: Number, default: 300 },
    // Textes UI i18n (audit 2026-08-04, A6-F1) : defaults FR, alimentés par
    // data-confirmation-modal-*-value avec t() depuis le layout admin —
    // règle CLAUDE.md « TEXTES UI EN i18n — Stimulus values ».
    defaultTitle: { type: String, default: "Confirmer l'action" },
    defaultMessage: { type: String, default: "Êtes-vous sûr de vouloir continuer ?" },
    archiveLabel: { type: String, default: "Archiver" },
    deleteLabel: { type: String, default: "Supprimer" }
  }

  // Configuration des variants
  variants = {
    soft_delete: {
      icon: "fa-archive",
      color: "amber",
      buttonClass: "bg-gradient-to-br from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-lg shadow-amber-500/30",
      headerGradient: "bg-gradient-to-br from-amber-400 to-amber-600"
    },
    hard_delete: {
      icon: "fa-trash-alt",
      color: "red",
      buttonClass: "bg-gradient-to-br from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white shadow-lg shadow-red-500/30",
      headerGradient: "bg-gradient-to-br from-red-400 to-red-600"
    }
  }

  connect() {
    this.handleEscape = this.handleEscape.bind(this)
    this.handleTurboSubmitEnd = this.handleTurboSubmitEnd.bind(this)
    this.activeModal = null
    this.previouslyFocusedElement = null
    this.requiredConfirmText = null
  }

  disconnect() {
    this.cleanup()
  }

  /**
   * Ouvre la modal avec les paramètres fournis
   * Appelé depuis n'importe quel bouton avec data-action="click->confirmation-modal#open"
   */
  open(event) {
    event.preventDefault()
    event.stopPropagation()

    const params = this.extractParams(event)
    this.createAndShowModal(params)
  }

  /**
   * Extrait les paramètres depuis l'élément trigger
   */
  extractParams(event) {
    const trigger = event.currentTarget

    return {
      title: trigger.dataset.confirmationModalTitleParam || this.defaultTitleValue,
      message: trigger.dataset.confirmationModalMessageParam || this.defaultMessageValue,
      url: trigger.dataset.confirmationModalUrlParam,
      variant: trigger.dataset.confirmationModalVariantParam || "hard_delete",
      confirmText: trigger.dataset.confirmationModalConfirmTextParam || null,
      consequences: this.parseConsequences(trigger.dataset.confirmationModalConsequencesParam)
    }
  }

  /**
   * Parse le JSON des conséquences
   */
  parseConsequences(consequencesJson) {
    if (!consequencesJson) return []

    try {
      return JSON.parse(consequencesJson)
    } catch (error) {
      console.error("Erreur parsing conséquences:", error)
      return []
    }
  }

  /**
   * Clone le template et affiche la modal
   */
  createAndShowModal(params) {
    const template = document.getElementById("confirmation-modal-template")
    if (!template) {
      console.error("Template confirmation-modal-template non trouvé")
      return
    }

    // Sauvegarder l'élément actif pour restaurer le focus
    this.previouslyFocusedElement = document.activeElement

    // Cloner le template
    const clone = template.content.cloneNode(true)
    this.activeModal = clone.querySelector('[role="dialog"]')

    // Remplir le contenu
    this.populateModal(clone, params)

    // Ajouter au DOM dans le container dédié
    const container = document.getElementById("confirmation-modal-container")
    if (!container) {
      console.error("Container confirmation-modal-container non trouvé")
      return
    }
    container.appendChild(clone)

    // Configurer la modal
    this.setupModal(params)

    // Animer l'ouverture
    requestAnimationFrame(() => {
      this.animateOpen()
    })
  }

  /**
   * Remplit le contenu de la modal avec les paramètres
   */
  populateModal(clone, params) {
    const variantConfig = this.variants[params.variant] || this.variants.hard_delete

    // Titre et message
    const titleEl = clone.querySelector('[data-confirmation-modal-target="title"]')
    const messageEl = clone.querySelector('[data-confirmation-modal-target="message"]')
    if (titleEl) titleEl.textContent = params.title
    if (messageEl) messageEl.textContent = params.message

    // Icône et couleur du header
    const iconContainer = clone.querySelector('[data-confirmation-modal-target="iconContainer"]')
    const iconEl = clone.querySelector('[data-confirmation-modal-target="icon"]')
    if (iconContainer) iconContainer.className = `inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${variantConfig.headerGradient}`
    if (iconEl) iconEl.className = `fas ${variantConfig.icon} text-white text-2xl`

    // URL du formulaire
    const formEl = clone.querySelector('[data-confirmation-modal-target="form"]')
    if (formEl && params.url) formEl.action = params.url

    // CSRF Token
    const csrfTokenEl = clone.querySelector('[data-confirmation-modal-target="csrfToken"]')
    if (csrfTokenEl) {
      const csrfMeta = document.querySelector('meta[name="csrf-token"]')
      csrfTokenEl.value = csrfMeta ? csrfMeta.content : ""
    }

    // Conséquences (si présentes) - utilisation sécurisée du DOM
    if (params.consequences && params.consequences.length > 0) {
      const consequencesSection = clone.querySelector('[data-confirmation-modal-target="consequencesSection"]')
      const consequencesList = clone.querySelector('[data-confirmation-modal-target="consequencesList"]')

      if (consequencesSection && consequencesList) {
        consequencesSection.classList.remove("hidden")
        this.populateConsequencesList(consequencesList, params.consequences)
      }
    }

    // Confirmation par texte (si requis)
    if (params.confirmText) {
      this.requiredConfirmText = params.confirmText
      const confirmTextSection = clone.querySelector('[data-confirmation-modal-target="confirmTextSection"]')
      const confirmTextDisplay = clone.querySelector('[data-confirmation-modal-target="confirmTextDisplay"]')

      if (confirmTextSection && confirmTextDisplay) {
        confirmTextSection.classList.remove("hidden")
        confirmTextDisplay.textContent = params.confirmText
      }
    } else {
      this.requiredConfirmText = null
    }

    // Avertissement selon le variant
    const warningSection = clone.querySelector('[data-confirmation-modal-target="warningSection"]')
    const softDeleteWarning = clone.querySelector('[data-confirmation-modal-target="softDeleteWarning"]')

    if (params.variant === "hard_delete" && warningSection) {
      warningSection.classList.remove("hidden")
    } else if (params.variant === "soft_delete" && softDeleteWarning) {
      softDeleteWarning.classList.remove("hidden")
    }

    // Bouton de soumission
    const submitButton = clone.querySelector('[data-confirmation-modal-target="submitButton"]')
    const submitIcon = clone.querySelector('[data-confirmation-modal-target="submitIcon"]')
    const submitText = clone.querySelector('[data-confirmation-modal-target="submitText"]')

    if (submitButton) {
      submitButton.className = `flex-1 btn btn-md transition-all duration-200 ${variantConfig.buttonClass}`
      // Activer/désactiver selon la présence de confirmText
      submitButton.disabled = !!params.confirmText
    }
    if (submitIcon) submitIcon.className = `fas ${variantConfig.icon} mr-2`
    if (submitText) {
      submitText.textContent = params.variant === "soft_delete"
        ? this.archiveLabelValue
        : this.deleteLabelValue
    }
  }

  /**
   * Remplit la liste des conséquences de manière sécurisée (sans innerHTML)
   */
  populateConsequencesList(listElement, consequences) {
    // Vider la liste existante
    while (listElement.firstChild) {
      listElement.removeChild(listElement.firstChild)
    }

    // Créer les éléments de manière sécurisée
    consequences.forEach(consequence => {
      const li = document.createElement("li")
      li.className = "flex items-center gap-2"

      const icon = document.createElement("i")
      icon.className = "fas fa-circle text-[6px] text-gray-400"

      const textNode = document.createTextNode(consequence)

      li.appendChild(icon)
      li.appendChild(textNode)
      listElement.appendChild(li)
    })
  }

  /**
   * Configure les événements de la modal
   */
  setupModal(params) {
    document.addEventListener("keydown", this.handleEscape)
    document.addEventListener("turbo:submit-end", this.handleTurboSubmitEnd)
    document.body.style.overflow = "hidden"

    // Setup du focus trap
    this.setupFocusTrap()
  }

  /**
   * Gère la fin de soumission Turbo pour fermer la modal
   */
  handleTurboSubmitEnd(event) {
    // Vérifier que la soumission vient du formulaire de la modal
    // On vérifie l'attribut data-confirmation-modal-target="form" car contains() peut échouer
    // si le DOM est modifié par la réponse Turbo
    const form = event.target
    if (form && form.getAttribute("data-confirmation-modal-target") === "form") {
      this.cleanup()
    }
  }

  /**
   * Anime l'ouverture de la modal
   */
  animateOpen() {
    const content = this.activeModal?.querySelector('[data-confirmation-modal-target="content"]')
    if (content) {
      content.classList.remove("scale-95", "opacity-0")
      content.classList.add("scale-100", "opacity-100")
    }

    // Focus le premier élément focusable après l'animation
    setTimeout(() => {
      this.focusFirstElement()
    }, this.animationDurationValue)
  }

  /**
   * Ferme la modal
   */
  close(event) {
    event?.preventDefault()
    event?.stopPropagation()

    if (!this.activeModal) return

    const content = this.activeModal.querySelector('[data-confirmation-modal-target="content"]')
    if (content) {
      content.classList.remove("scale-100", "opacity-100")
      content.classList.add("scale-95", "opacity-0")
    }

    setTimeout(() => {
      this.cleanup()
    }, this.animationDurationValue)
  }

  /**
   * Nettoie les ressources
   */
  cleanup() {
    document.removeEventListener("keydown", this.handleEscape)
    document.removeEventListener("turbo:submit-end", this.handleTurboSubmitEnd)
    document.body.style.overflow = ""

    if (this.activeModal) {
      this.activeModal.remove()
      this.activeModal = null
    }

    // Restaurer le focus
    if (this.previouslyFocusedElement && this.previouslyFocusedElement.focus) {
      this.previouslyFocusedElement.focus()
      this.previouslyFocusedElement = null
    }

    this.requiredConfirmText = null
  }

  /**
   * Gère la touche Escape
   */
  handleEscape(event) {
    if (event.key === "Escape") {
      this.close(event)
    }
  }

  /**
   * Empêche la propagation du clic sur le contenu
   */
  stopPropagation(event) {
    event.stopPropagation()
  }

  /**
   * Valide l'input de confirmation
   */
  validateInput(event) {
    const input = event.target
    const submitButton = this.activeModal?.querySelector('[data-confirmation-modal-target="submitButton"]')

    if (!submitButton || !this.requiredConfirmText) return

    const isValid = input.value === this.requiredConfirmText
    submitButton.disabled = !isValid

    // Feedback visuel
    if (input.value.length > 0) {
      if (isValid) {
        input.classList.remove("border-red-300", "focus:ring-red-500", "focus:border-red-500")
        input.classList.add("border-green-300", "focus:ring-green-500", "focus:border-green-500")
      } else {
        input.classList.remove("border-green-300", "focus:ring-green-500", "focus:border-green-500")
        input.classList.add("border-red-300", "focus:ring-red-500", "focus:border-red-500")
      }
    } else {
      input.classList.remove("border-red-300", "border-green-300", "focus:ring-red-500", "focus:ring-green-500", "focus:border-red-500", "focus:border-green-500")
    }
  }

  /**
   * Setup du focus trap (RGAA 4.1)
   */
  setupFocusTrap() {
    if (!this.activeModal) return

    this.activeModal.addEventListener("keydown", (event) => {
      if (event.key !== "Tab") return

      const focusableElements = this.getFocusableElements()
      if (focusableElements.length === 0) return

      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault()
        lastElement.focus()
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
      }
    })
  }

  /**
   * Récupère les éléments focusables dans la modal
   */
  getFocusableElements() {
    if (!this.activeModal) return []

    const selectors = [
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(", ")

    return Array.from(this.activeModal.querySelectorAll(selectors))
      .filter(el => el.offsetWidth > 0 && el.offsetHeight > 0)
  }

  /**
   * Focus le premier élément focusable
   */
  focusFirstElement() {
    const focusableElements = this.getFocusableElements()

    // Prioriser l'input de confirmation s'il existe et est visible
    const confirmInput = this.activeModal?.querySelector('[data-confirmation-modal-target="confirmTextInput"]')
    if (confirmInput && !confirmInput.closest(".hidden")) {
      confirmInput.focus()
      return
    }

    // Sinon focus le bouton annuler
    const cancelButton = this.activeModal?.querySelector('[data-confirmation-modal-target="cancelButton"]')
    if (cancelButton) {
      cancelButton.focus()
      return
    }

    // Fallback sur le premier élément
    if (focusableElements.length > 0) {
      focusableElements[0].focus()
    }
  }
}
